<template>
  <ol
    v-if="entities.length"
    class="tw:list-none tw:m-0 tw:pl-0 tw:pr-2"
    data-testid="entity-sentiment-list"
  >
    <li
      v-if="!hideHeading"
      class="tw:font-bold tw:text-sm tw:mb-2 tw:text-denim-900"
      data-testid="entity-sentiment-list-heading"
    />

    <template v-if="searchable || sortable">
      <li
        class="tw:flex tw:items-end tw:gap-2 tw:mb-3"
        data-testid="entity-sentiment-list-controls"
      >
        <div
          v-if="searchable"
          class="tw:flex tw:flex-col tw:gap-1 tw:w-1/2"
          data-testid="entity-sentiment-list-search-wrapper"
        >
          <label
            class="tw:pb-1 tw:text-sm tw:font-normal tw:text-denim-700"
            for="entity-sentiment-search"
          >
            {{ getLocaleText("entity_sentiment_list.search_label") }}
          </label>
          <BaseSearchInput
            id="entity-sentiment-search"
            v-model="searchQuery"
            clearable
            data-testid="entity-sentiment-list-search"
            :placeholder="
              getLocaleText('entity_sentiment_list.search_placeholder')
            "
          />
        </div>

        <div
          v-if="sortable"
          class="tw:ml-auto tw:flex tw:items-center tw:gap-2"
          data-testid="entity-sentiment-list-sort"
        >
          <span class="tw:text-sm tw:font-normal tw:text-denim-700 tw:shrink-0">
            {{ getLocaleText("entity_sentiment_list.sort_by_label") }}
          </span>
          <BaseButtonGroup>
            <BaseButtonDropdown
              v-model="sortDropdownVisible"
              bordered
              chevron
              close-on-click
              color="white"
              :label="
                getLocaleText('entity_sentiment_list.sort_sentiment_label')
              "
              new-design
              new-dropdown
              placement="bottom-start"
            >
              <BaseButtonList
                :model-value="selectedSortOption"
                :options="sortOptions"
                @update:model-value="sortOptionSelected"
              />
            </BaseButtonDropdown>

            <BaseButton
              bordered
              color="white"
              :data-testid="`entity-sentiment-sort-${sortDesc ? 'desc' : 'asc'}`"
              :icon="sortDirectionIcon"
              icon-size="sm"
              new-design
              @click="toggleSort"
            >
              <BaseStreemTooltip>
                {{
                  sortDesc
                    ? getLocaleText("entity_sentiment_list.sort_most_positive")
                    : getLocaleText("entity_sentiment_list.sort_most_negative")
                }}
              </BaseStreemTooltip>
            </BaseButton>
          </BaseButtonGroup>
        </div>
      </li>
    </template>

    <li
      v-for="(entity, index) in displayedEntities"
      :key="`${index}-${entity.type}-${entity.name}`"
      class="tw:flex tw:items-center tw:justify-between tw:gap-4 tw:py-1 tw:border-b tw:border-denim-100 tw:last:border-b-0"
      data-testid="entity-sentiment-list-row"
    >
      <div class="tw:flex tw:items-center tw:gap-1.5 tw:min-w-0">
        <BaseLineClamp
          class="tw:text-sm"
          data-testid="entity-sentiment-list-name"
          :lines="1"
          :tooltip="entity.name"
        >
          {{ entity.name }}
        </BaseLineClamp>
      </div>

      <span
        class="tw:shrink-0 tw:text-sm tw:font-medium"
        data-testid="entity-sentiment-list-label"
        :style="{ color: sentimentColor(entity.sentiment) }"
      >
        {{ entity.sentiment_label }}
      </span>
    </li>
  </ol>
</template>

<script setup lang="ts">
import { type DesignSystemIcons } from "design-system/types";
import { computed, ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import {
  BaseButton,
  BaseButtonDropdown,
  BaseButtonGroup,
  BaseButtonList,
  BaseLineClamp,
  BaseSearchInput,
  BaseStreemTooltip,
} from "shared/components/base";
import { sentimentOptions } from "shared/components/core/pickers/SentimentPicker";
import type { EntitySentiment } from "shared/types/mentions";

interface EntitySentimentListProps {
  entities: EntitySentiment[];
  hideHeading?: boolean;
  searchable?: boolean;
  sortable?: boolean;
}

const ENTITY_TYPE_ICONS: Record<string, DesignSystemIcons> = {
  Location: "location",
  Organization: "company",
  Person: "user",
};

const SORT_OPTIONS = [{ name: "sentiment", label: "Sentiment" }] as const;

type SortOption = (typeof SORT_OPTIONS)[number];

const props = defineProps<EntitySentimentListProps>();

const searchQuery = ref<string>("");
const sortDesc = ref<boolean>(false);
const sortDropdownVisible = ref<boolean>(false);
const selectedSortOption = ref<SortOption>(SORT_OPTIONS[0]);

const sortOptions = [...SORT_OPTIONS];

const computedEntities = computed(() =>
  props.entities.map((entity) => ({
    ...entity,
    icon: entity.type ? (ENTITY_TYPE_ICONS[entity.type] ?? null) : null,
  }))
);

const displayedEntities = computed(() => {
  let result = computedEntities.value;

  if (props.searchable && searchQuery.value.trim()) {
    const query = searchQuery.value.trim().toLowerCase();

    result = result.filter((entity) =>
      entity.name?.toLowerCase().includes(query)
    );
  }

  if (props.sortable) {
    result = [...result].sort((entityA, entityB) =>
      sortDesc.value
        ? entityB.sentiment - entityA.sentiment
        : entityA.sentiment - entityB.sentiment
    );
  }

  return result;
});

const sortDirectionIcon = computed(() =>
  sortDesc.value ? "fas fa-sort-amount-down" : "fas fa-sort-amount-up"
);

function sortOptionSelected(option: SortOption): void {
  selectedSortOption.value = option;
  sortDropdownVisible.value = false;
}

function toggleSort(): void {
  sortDesc.value = !sortDesc.value;
}

function sentimentColor(sentiment: number): string {
  const option = sentimentOptions.find((opt) => opt.inRange(sentiment));

  return option?.color ?? sentimentOptions[2].color;
}
</script>
